import React, { useState, useEffect } from "react";
import {
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  IconographyThemes,
  Button,
  Pagination,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  getCategoriesSagaAction,
  createCategorySagaAction,
  updateCategorySagaAction,
  deleteCategorySagaAction,
} from "../../../../../../redux/saga/project-validation/saga-actions";
import { CategoryType } from "../../../../../../redux/slices/project-validation/Types";
import AddCategoryModal from "./AddCategoryModal";
import DeleteModal from "../../../../../../common/components/DeleteModal";
import partnerValidationImage from "../../../../../../common/assets/images/partner-validation.png";
import "./Categories.scss";

const Categories: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const { categories, status } = useAppSelector(
    (state) => state.project_validation
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryType | null>(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(null);

  const handleDeleteClick = (categoryId: number) => {
    setDeletingCategoryId(categoryId);
    setIsDeleteModalOpen(true);
  };
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    dispatch(getCategoriesSagaAction());
  }, [dispatch]);

  // Pagination logic
  const totalRecords = categories.length;
  const startIndex = (currentPageNumber - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCategories = categories.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPageNumber(page);
  };

  const onPageSizesClick = (_e: React.MouseEvent<HTMLElement>, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const handleOpenModal = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const handleEditCategory = (category: CategoryType) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deletingCategoryId) {
      dispatch(deleteCategorySagaAction(deletingCategoryId));
    }
    setIsDeleteModalOpen(false);
    setDeletingCategoryId(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletingCategoryId(null);
  };

  const handleSaveCategory = (categoryData: { categoryName: string; description: string }) => {
    if (editingCategory) {
      // Update existing category
      dispatch(
        updateCategorySagaAction({
          id: editingCategory.ID,
          category_name: categoryData.categoryName,
          description: categoryData.description || null,
        })
      );
    } else {
      // Add new category
      dispatch(
        createCategorySagaAction({
          category_name: categoryData.categoryName,
          description: categoryData.description || null,
        })
      );
    }
    // Modal will show success state, then close itself
  };

  return (
    <>
      <div className="categories-content">
        {status.categoriesLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "50px",
            }}
          >
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : (
          <>
            {categories.length === 0 ? (
              /* Empty State */
              <>
                <div className="empty-state-header">
                  <Typography className="empty-state-text">
                    No categories have been created yet. Please create a
                    category before adding requirements.
                  </Typography>
                </div>
                <div className="categories-header">
                  <Button
                    variant="primary"
                    label="Add category"
                    icon={<Iconography icon="plus" />}
                    onClick={handleOpenModal}
                    disabled={readOnly}
                  />
                </div>
                <div className="empty-state">
                  <img src={partnerValidationImage} alt="No categories" />
                </div>
              </>
            ) : (
              <>
                {/* Header with Add button */}
                <div className="categories-header-with-data">
                  <Button
                    variant="primary"
                    label="Add category"
                    icon={<Iconography icon="plus" />}
                    onClick={handleOpenModal}
                    disabled={readOnly}
                  />
                </div>
                <TableContainer component={Paper} className="categories-table">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell size="small">Category Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell size="small">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCategories.map((category) => (
                        <TableRow key={category.ID}>
                          <TableCell className="category-name-cell" size="small">
                            {category.category_name}
                          </TableCell>
                          <TableCell className="description-cell">
                            {category.description || ""}
                          </TableCell>
                          <TableCell size="small">
                            <Button
                              variant="flat"
                              icon={<Iconography icon="pen" />}
                              onClick={() => handleEditCategory(category)}
                              disabled={readOnly}
                            />
                            <Button
                              variant="flat"
                              icon={<Iconography icon="trashCan" />}
                              onClick={() => handleDeleteClick(category.ID)}
                              className="delete-button"
                              disabled={readOnly}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalRecords > 0 && (
                  <div
                    className="hid-pagination-container"
                    style={{ marginTop: "20px" }}
                  >
                    <Pagination
                      currentPageNumber={currentPageNumber}
                      totalRecords={totalRecords}
                      pageSize={pageSize}
                      onPageNumberClick={handlePageChange}
                      onPageSizesClick={onPageSizesClick}
                      pageSizes={[
                        { label: "10", value: "10" },
                        { label: "20", value: "20" },
                        { label: "50", value: "50" },
                      ]}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <AddCategoryModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCategory}
        editData={editingCategory}
      />
      {isDeleteModalOpen && (
        <DeleteModal
          setDeleteOpen={setIsDeleteModalOpen}
          onDeleteButtonClick={handleDeleteConfirm}
          deleteButtonLoading={status.deleteCategoryLoading}
          optionalHeader="Delete Category"
          message="Are you sure you want to delete this category? This action cannot be undone."
        />
      )}
    </>
  );
};

export default Categories;
